import React from 'react';
import { defineMessages } from 'react-intl-next';

import { DefaultExtensionProvider } from '@atlaskit/editor-common/extensions';
import { token } from '@atlaskit/tokens';

import type { CustomSitesExtensionProviderProps } from '@atlassian/company-hub-common';

import { LoadableAfterPaint } from '@confluence/loadable';

import {
	hubElementPlaceholderExtensionKey,
	hubElementPlaceholderExtensionType,
} from './hubElementPlaceholderConstants';

const HubElementPlaceholderExtension = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-HubElementPlaceholderExtension" */ './HubElementPlaceholderExtension'
			)
		).HubElementPlaceholderExtension,
});

const i18n = defineMessages({
	title: {
		id: 'custom-sites-extensions.hubElementPlaceholder.manifest.extension-title.non-final',
		defaultMessage: 'Hub element placeholder',
		description:
			'Title of hub element placeholder extension to be displayed the macro is presented in the editor.',
	},
});

export const hubElementPlaceholderExtensionProvider = async ({
	contentId,
	editorActions,
	openConfigPanel,
	intl,
}: CustomSitesExtensionProviderProps) => {
	const type = hubElementPlaceholderExtensionType;
	const title = intl.formatMessage(i18n.title);

	const icons = {
		'48': () =>
			import(
				/* webpackChunkName: "loadable-atlaskitIconCorePremium" */ '@atlaskit/icon/core/premium'
			).then(({ default: PremiumIcon }) => () => (
				<PremiumIcon color={token('color.icon.subtle')} label="" spacing="spacious" />
			)),
	};

	const update = async () => {
		const selectedNode = editorActions?.getSelectedNode()?.toJSON();
		const selectedLocalId = selectedNode?.attrs?.localId;

		if (!!selectedLocalId && openConfigPanel) {
			openConfigPanel(selectedLocalId);
		}
	};

	const render =
		async () =>
		({ node }) =>
			// Only render the extension if we are in the editor
			editorActions ? (
				<HubElementPlaceholderExtension
					extensionNode={node}
					editorActions={editorActions}
					openConfigPanel={openConfigPanel}
					contentId={contentId}
				/>
			) : null;

	return new DefaultExtensionProvider([
		{
			key: hubElementPlaceholderExtensionKey,
			icons,
			title,
			type,
			description: '',
			modules: {
				nodes: {
					default: {
						type: 'extension',
						render,
						update,
					},
				},
			},
		},
	]);
};
